// @flow strict

import axios from 'axios';
import { camelizeKeys, decamelizeKeys } from 'humps';

import { API_URL, API_VER } from 'env';

import { logoutUser } from 'services/Authentication/thunks';
import { store } from '../index';

import { legacyBackwardMappers } from './legacyMappers';
import { LOGOUT_API_ENDPOINT, WHOAMI_API_ENDPOINT } from './Authentication/resources';

/**
 * Requests a URL, returning a promise.
 *
 * Axios by default will reject a promise if it is not between status codes 200-300
 * (This can be modified by implementing the validateStatus method)
 *
 * @param {string} url       The URL we want to request
 * @param {object} [options] The options we want to pass to "fetch"
 * @param {apiVer} [optional] Optional api version (eg. /api/v2/)
 * @param {humps} [optional] Set to false to not camelize/decamelize keys
 *
 * @return {object}           An object containing either "data" or "err"
 */

export default function request(
    url: string,
    options?: {
        method?: ?string,
        data?: ?Object,
        apiUrl?: ?string,
    } = {
        method: 'GET',
        data: {},
        apiUrl: undefined,
    },
    params?: Object = {},
    apiVer?: string = API_VER,
    humps?: boolean = true,
    additionalParams?: Object = {}
) {
    const apiUrl = options.apiUrl || API_URL;
    const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Authorization: `Bearer ${localStorage.getItem('api_token') || ''}`,
    };

    return axios({
        url: `${apiUrl}${apiVer}${url}`,
        method: options.method ? options.method : 'GET',
        data: humps ? decamelizeKeys(options.data) : options.data,
        headers,
        params,
        ...additionalParams,
    })
        .then((response: Object) =>
            Promise.resolve(humps ? camelizeKeys(response.data) : response.data)
        )
        .then(legacyBackwardMappers)
        .catch((error: Object) => {
            let returnError = {};
            if (error.response) {
                // We received a response from the server.
                // Check if the user is unauthorized or forbidden
                if (error.response.status === 401 || error.response.status === 403) {
                    const logoutOutClientOnly = url === LOGOUT_API_ENDPOINT; // if the logout request failed, we need to logout the user from the client only

                    store.dispatch(logoutUser(logoutOutClientOnly));
                }

                if (error.response.data) {
                    // comes from our backend formatted errors.
                    returnError = error.response.data;
                } else {
                    returnError = {
                        error: 'request.generic_exception',
                        message: error.response.statusText,
                        extra: error,
                    };
                }
            } else if (error.request) {
                // We did not receive a response from the server, but we did a request.

                if (url === WHOAMI_API_ENDPOINT) {
                    // The user is logged in with the firebase frontend client, but we could not retrieve the user details from the backend.
                    // We need to logout the user since they won't be able to properly use the application anyways.
                    store.dispatch(logoutUser());
                }

                console.error('Generic error: Did not receive a server response.', error);
                returnError = {
                    error: 'request.empty_response',
                    message: 'Did not receive a server response.',
                    extra: error,
                };
            } else {
                // We weren't even able to make a request.
                console.error('Generic error: Could not make a request.', error);
                returnError = {
                    error: 'request.failed_request',
                    message: 'Could not make a request to the server.',
                    extra: error,
                };
            }

            return Promise.reject(humps ? camelizeKeys(returnError) : returnError);
        });
}

/**
 * Sends a request to the given URL, returning a promise. This function is specifically for cloud functions / apis other than ms-backend since the options are different.
 *
 * @param {string} url       The URL we want to request
 * @param {object} [options] The options we want to pass to "fetch"
 * @param {apiVer} [optional] Optional api version (eg. /api/v2/)
 * @param {humps} [optional] Set to false to not camelize/decamelize keys
 *
 * @return {object}           An object containing either "data" or "err"
 */
export function cloudFunctionApiRequest(
    url: string,
    options: {
        method?: ?string,
        data?: ?Object,
        apiUrl: string, // required
    } = {
        method: 'GET',
        data: {},
        apiUrl, // required
    },
    params?: Object = {},
    apiVer?: string = API_VER,
    humps?: boolean = false, // do not camelize/decamelize keys for cloud functions
    additionalParams?: Object = {}
) {
    return request(url, options, params, apiVer, humps, additionalParams);
}
